// CoursesFooter.js - Modern footer for online courses website
import { DEVICES } from '../../../constants/breakpoints';

export const createCoursesFooterElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Class names
  const footerClass = 'courses-footer';
  const containerClass = 'courses-footer-container';
  const topSectionClass = 'courses-footer-top';
  const brandSectionClass = 'courses-footer-brand';
  const logoClass = 'courses-footer-logo';
  const brandDescClass = 'courses-footer-brand-desc';
  const socialLinksClass = 'courses-footer-social';
  const socialLinkClass = 'courses-footer-social-link';
  const linksGridClass = 'courses-footer-links-grid';
  const linkColumnClass = 'courses-footer-link-column';
  const columnTitleClass = 'courses-footer-column-title';
  const linkListClass = 'courses-footer-link-list';
  const linkItemClass = 'courses-footer-link-item';
  const linkClass = 'courses-footer-link';
  const newsletterSectionClass = 'courses-footer-newsletter';
  const newsletterTitleClass = 'courses-footer-newsletter-title';
  const newsletterDescClass = 'courses-footer-newsletter-desc';
  const newsletterFormClass = 'courses-footer-newsletter-form';
  const newsletterInputClass = 'courses-footer-newsletter-input';
  const newsletterButtonClass = 'courses-footer-newsletter-button';
  const bottomSectionClass = 'courses-footer-bottom';
  const copyrightClass = 'courses-footer-copyright';
  const bottomLinksClass = 'courses-footer-bottom-links';
  const bottomLinkClass = 'courses-footer-bottom-link';

  // Desktop Styles
  const footerStyles = {
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    paddingTop: '80px',
    paddingBottom: '40px',
    fontFamily: '"Inter", sans-serif',
    position: 'relative',
    overflow: 'hidden'
  };

  const containerStyles = {
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: '40px',
    paddingRight: '40px',
    position: 'relative',
    zIndex: '2'
  };

  const topSectionStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr 1fr',
    gap: '60px',
    marginBottom: '60px'
  };

  const brandSectionStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  };

  const logoStyles = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#ffffff',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '16px'
  };

  const brandDescStyles = {
    fontSize: '16px',
    color: '#94a3b8',
    lineHeight: '1.6',
    marginBottom: '24px'
  };

  const socialLinksStyles = {
    display: 'flex',
    gap: '16px'
  };

  const socialLinkStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: '#334155',
    borderRadius: '8px',
    color: '#e2e8f0',
    textDecoration: 'none',
    fontSize: '18px',
    transition: 'all 0.3s ease'
  };

  const linksGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '40px'
  };

  const linkColumnStyles = {
    display: 'flex',
    flexDirection: 'column'
  };

  const columnTitleStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '20px'
  };

  const linkListStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    listStyle: 'none',
    margin: '0',
    padding: '0'
  };

  const linkItemStyles = {
    margin: '0'
  };

  const linkStyles = {
    color: '#94a3b8',
    textDecoration: 'none',
    fontSize: '15px',
    transition: 'color 0.3s ease',
    display: 'block'
  };

  const newsletterSectionStyles = {
    backgroundColor: '#334155',
    borderRadius: '16px',
    padding: '32px',
    textAlign: 'center'
  };

  const newsletterTitleStyles = {
    fontSize: '20px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '12px'
  };

  const newsletterDescStyles = {
    fontSize: '14px',
    color: '#94a3b8',
    marginBottom: '24px',
    lineHeight: '1.5'
  };

  const newsletterFormStyles = {
    display: 'flex',
    gap: '12px',
    maxWidth: '300px',
    margin: '0 auto'
  };

  const newsletterInputStyles = {
    flex: '1',
    padding: '12px 16px',
    backgroundColor: '#475569',
    border: '1px solid #64748b',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.3s ease'
  };

  const newsletterButtonStyles = {
    padding: '12px 20px',
    backgroundColor: '#6366f1',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    whiteSpace: 'nowrap'
  };

  const bottomSectionStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '40px',
    borderTop: '1px solid #334155',
    flexWrap: 'wrap',
    gap: '20px'
  };

  const copyrightStyles = {
    fontSize: '14px',
    color: '#64748b'
  };

  const bottomLinksStyles = {
    display: 'flex',
    gap: '32px',
    listStyle: 'none',
    margin: '0',
    padding: '0'
  };

  const bottomLinkStyles = {
    color: '#94a3b8',
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'color 0.3s ease'
  };

  // Register styles
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    newRegistry[DEVICES.DESKTOP].set(footerClass, footerStyles);
    newRegistry[DEVICES.DESKTOP].set(containerClass, containerStyles);
    newRegistry[DEVICES.DESKTOP].set(topSectionClass, topSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(brandSectionClass, brandSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(logoClass, logoStyles);
    newRegistry[DEVICES.DESKTOP].set(brandDescClass, brandDescStyles);
    newRegistry[DEVICES.DESKTOP].set(socialLinksClass, socialLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(socialLinkClass, socialLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(linksGridClass, linksGridStyles);
    newRegistry[DEVICES.DESKTOP].set(linkColumnClass, linkColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(columnTitleClass, columnTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(linkListClass, linkListStyles);
    newRegistry[DEVICES.DESKTOP].set(linkItemClass, linkItemStyles);
    newRegistry[DEVICES.DESKTOP].set(linkClass, linkStyles);
    newRegistry[DEVICES.DESKTOP].set(newsletterSectionClass, newsletterSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(newsletterTitleClass, newsletterTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(newsletterDescClass, newsletterDescStyles);
    newRegistry[DEVICES.DESKTOP].set(newsletterFormClass, newsletterFormStyles);
    newRegistry[DEVICES.DESKTOP].set(newsletterInputClass, newsletterInputStyles);
    newRegistry[DEVICES.DESKTOP].set(newsletterButtonClass, newsletterButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(bottomSectionClass, bottomSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(copyrightClass, copyrightStyles);
    newRegistry[DEVICES.DESKTOP].set(bottomLinksClass, bottomLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(bottomLinkClass, bottomLinkStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(topSectionClass, { 
      gridTemplateColumns: '1fr 1fr', 
      gap: '40px',
      marginBottom: '50px'
    });
    newRegistry[DEVICES.TABLET].set(linksGridClass, { 
      gridTemplateColumns: 'repeat(2, 1fr)', 
      gap: '30px' 
    });
    newRegistry[DEVICES.TABLET].set(newsletterSectionClass, { 
      gridColumn: '1 / -1',
      padding: '24px'
    });
    newRegistry[DEVICES.TABLET].set(bottomSectionClass, { 
      flexDirection: 'column',
      textAlign: 'center',
      gap: '16px'
    });
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(footerClass, { 
      paddingTop: '60px',
      paddingBottom: '30px'
    });
    newRegistry[DEVICES.MOBILE].set(containerClass, { 
      paddingLeft: '20px',
      paddingRight: '20px'
    });
    newRegistry[DEVICES.MOBILE].set(topSectionClass, { 
      gridTemplateColumns: '1fr',
      gap: '40px',
      marginBottom: '40px'
    });
    newRegistry[DEVICES.MOBILE].set(linksGridClass, { 
      gridTemplateColumns: '1fr',
      gap: '30px'
    });
    newRegistry[DEVICES.MOBILE].set(newsletterFormClass, { 
      flexDirection: 'column',
      maxWidth: '100%'
    });
    newRegistry[DEVICES.MOBILE].set(bottomLinksClass, { 
      flexDirection: 'column',
      gap: '16px',
      textAlign: 'center'
    });
    
    return newRegistry;
  });

  // Add classes to global registry
  setGlobalClassNames(prev => new Set([
    ...prev,
    footerClass, containerClass, topSectionClass, brandSectionClass,
    logoClass, brandDescClass, socialLinksClass, socialLinkClass,
    linksGridClass, linkColumnClass, columnTitleClass, linkListClass,
    linkItemClass, linkClass, newsletterSectionClass, newsletterTitleClass,
    newsletterDescClass, newsletterFormClass, newsletterInputClass,
    newsletterButtonClass, bottomSectionClass, copyrightClass,
    bottomLinksClass, bottomLinkClass
  ]));

  // Generate IDs
  const baseId = Date.now();
  const footerId = baseId;
  const containerId = baseId + 1;
  const topSectionId = baseId + 2;
  const brandSectionId = baseId + 3;
  const logoId = baseId + 4;
  const logoImageId = baseId + 5;
  const brandDescId = baseId + 6;
  const socialLinksId = baseId + 7;
  const facebookLinkId = baseId + 8;
  const twitterLinkId = baseId + 9;
  const linkedinLinkId = baseId + 10;
  const instagramLinkId = baseId + 11;
  const linksGridId = baseId + 12;
  
  // Courses column
  const coursesColumnId = baseId + 13;
  const coursesTitleId = baseId + 14;
  const coursesListId = baseId + 15;
  const webDevLinkId = baseId + 16;
  const dataScientLinkId = baseId + 17;
  const digitalMarketingLinkId = baseId + 18;
  const uiUxLinkId = baseId + 19;
  const mobileLinkId = baseId + 20;
  const aiMlLinkId = baseId + 21;
  const electricianLinkId = baseId + 80;

  // Company column
  const companyColumnId = baseId + 22;
  const companyTitleId = baseId + 23;
  const companyListId = baseId + 24;
  const aboutLinkId = baseId + 25;
  const careersLinkId = baseId + 26;
  const blogLinkId = baseId + 27;
  const pressLinkId = baseId + 28;
  const partnersLinkId = baseId + 29;

  // Support column
  const supportColumnId = baseId + 30;
  const supportTitleId = baseId + 31;
  const supportListId = baseId + 32;
  const helpCenterLinkId = baseId + 33;
  const contactLinkId = baseId + 34;
  const communityLinkId = baseId + 35;
  const faqLinkId = baseId + 36;
  const statusLinkId = baseId + 37;

  // Newsletter section
  const newsletterSectionId = baseId + 38;
  const newsletterTitleId = baseId + 39;
  const newsletterDescId = baseId + 40;
  const newsletterFormId = baseId + 41;
  const newsletterInputId = baseId + 42;
  const newsletterButtonId = baseId + 43;

  // Bottom section
  const bottomSectionId = baseId + 44;
  const copyrightId = baseId + 45;
  const bottomLinksId = baseId + 46;
  const privacyLinkId = baseId + 47;
  const termsLinkId = baseId + 48;
  const cookiesLinkId = baseId + 49;

  const elements = [
    // Footer
    {
      id: footerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Courses Footer',
      parentId: null,
      className: footerClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: { 'data-element': 'courses-footer' }
    },

    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Container',
      parentId: footerId,
      hierarchyName: getHierarchyName('div'),
      className: containerClass,
      styles: {},
      children: [topSectionId, bottomSectionId],
      text: '',
      attributes: { 'data-element': 'footer-container' }
    },

    // Top Section
    {
      id: topSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Top Section',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: topSectionClass,
      styles: {},
      children: [brandSectionId, linksGridId, newsletterSectionId],
      text: '',
      attributes: { 'data-element': 'footer-top' }
    },

    // Brand Section
    {
      id: brandSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Brand Section',
      parentId: topSectionId,
      hierarchyName: getHierarchyName('div'),
      className: brandSectionClass,
      styles: {},
      children: [logoId, brandDescId, logoImageId + 50, socialLinksId],
      text: '',
      attributes: { 'data-element': 'footer-brand' }
    },

    // Logo Link
    {
      id: logoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Logo Link',
      parentId: brandSectionId,
      hierarchyName: getHierarchyName('a'),
      styles: {
        display: 'inline-block',
        marginBottom: '16px'
      },
      children: [logoImageId],
      text: '',
      linkTo: 'index',
      attributes: { 
        'data-element': 'footer-logo-link',
        'aria-label': 'SNIT Home'
      }
    },

    // Logo Image
    {
      id: logoImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Footer Logo Image',
      parentId: logoId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '160px',
        height: 'auto'
      },
      src: 'https://res.cloudinary.com/do8tgpf80/image/upload/SNIT_Logo_1_kczdpy.png',
      children: [],
      text: '',
      attributes: { 
        'data-element': 'footer-logo',
        'alt': 'SNIT Logo'
      }
    },

    // Brand Description
    {
      id: brandDescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Footer Brand Description',
      parentId: brandSectionId,
      hierarchyName: getHierarchyName('p'),
      className: brandDescClass,
      styles: {},
      children: [],
      text: 'Empowering students with practical vocational skills and professional training for successful careers in various industries.',
      attributes: { 'data-element': 'footer-brand-desc' }
    },

    // Contact Info Container
    {
      id: logoImageId + 50,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Contact Info',
      parentId: brandSectionId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginBottom: '20px'
      },
      children: [logoImageId + 51, logoImageId + 52, logoImageId + 53],
      text: '',
      attributes: { 'data-element': 'footer-contact-info' }
    },

    // Address Info
    {
      id: logoImageId + 51,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Footer Address',
      parentId: logoImageId + 50,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '14px',
        color: '#94a3b8',
        margin: '0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '�� Chak#: 102 JB Burj Village Faisalabad, Pakistan',
      attributes: { 'data-element': 'footer-address' }
    },

    // Phone Info
    {
      id: logoImageId + 52,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Footer Phone',
      parentId: logoImageId + 50,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '14px',
        color: '#94a3b8',
        margin: '0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '📞 03012491171',
      attributes: { 'data-element': 'footer-phone' }
    },

    // Email Info
    {
      id: logoImageId + 53,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Footer Email',
      parentId: logoImageId + 50,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '14px',
        color: '#94a3b8',
        margin: '0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '📧 snit.org@hotmail.com',
      attributes: { 'data-element': 'footer-email' }
    },

    // Social Links
    {
      id: socialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Social Links',
      parentId: brandSectionId,
      hierarchyName: getHierarchyName('div'),
      className: socialLinksClass,
      styles: {},
      children: [facebookLinkId, twitterLinkId, linkedinLinkId, instagramLinkId],
      text: '',
      attributes: { 'data-element': 'footer-social' }
    },

    // Facebook Link
    {
      id: facebookLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Facebook Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [facebookLinkId + 100],
      text: '',
      linkTo: 'https://www.facebook.com/share/16Q7ppag35/',
      attributes: { 
        'href': 'https://www.facebook.com/share/16Q7ppag35/',
        'aria-label': 'Facebook',
        'data-element': 'social-link'
      }
    },

    // Facebook Icon
    {
      id: facebookLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Facebook Icon',
      parentId: facebookLinkId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '18px',
        height: '18px'
      },
      src: 'https://api.iconify.design/mdi:facebook.svg?color=%23e2e8f0',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Facebook'
      }
    },

    // WhatsApp Link
    {
      id: twitterLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'WhatsApp Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [twitterLinkId + 100],
      text: '',
      linkTo: 'https://whatsapp.com/channel/0029Vb4nVx57oQhazj6xAt41',
      attributes: { 
        'href': 'https://whatsapp.com/channel/0029Vb4nVx57oQhazj6xAt41',
        'aria-label': 'WhatsApp',
        'data-element': 'social-link'
      }
    },

    // WhatsApp Icon
    {
      id: twitterLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'WhatsApp Icon',
      parentId: twitterLinkId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '18px',
        height: '18px'
      },
      src: 'https://api.iconify.design/mdi:whatsapp.svg?color=%23e2e8f0',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'WhatsApp'
      }
    },

    // TikTok Link
    {
      id: linkedinLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'TikTok Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [linkedinLinkId + 100],
      text: '',
      linkTo: 'https://www.tiktok.com/@snitfaisalabad?_t=ZS-8wjL0zMp8HR&_r=1',
      attributes: { 
        'href': 'https://www.tiktok.com/@snitfaisalabad?_t=ZS-8wjL0zMp8HR&_r=1',
        'aria-label': 'TikTok',
        'data-element': 'social-link'
      }
    },

    // TikTok Icon
    {
      id: linkedinLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'TikTok Icon',
      parentId: linkedinLinkId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '18px',
        height: '18px'
      },
      src: 'https://api.iconify.design/simple-icons:tiktok.svg?color=%23e2e8f0',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'TikTok'
      }
    },

    // Instagram Link
    {
      id: instagramLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Instagram Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [instagramLinkId + 100],
      text: '',
      linkTo: 'https://www.instagram.com/snitfaisalabad?igsh=MW5hMXNnNnJtZjg1bw==',
      attributes: { 
        'href': 'https://www.instagram.com/snitfaisalabad?igsh=MW5hMXNnNnJtZjg1bw==',
        'aria-label': 'Instagram',
        'data-element': 'social-link'
      }
    },

    // Instagram Icon
    {
      id: instagramLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Instagram Icon',
      parentId: instagramLinkId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '18px',
        height: '18px'
      },
      src: 'https://api.iconify.design/mdi:instagram.svg?color=%23e2e8f0',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Instagram'
      }
    },

    // Links Grid
    {
      id: linksGridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Links Grid',
      parentId: topSectionId,
      hierarchyName: getHierarchyName('div'),
      className: linksGridClass,
      styles: {},
      children: [coursesColumnId, companyColumnId, supportColumnId],
      text: '',
      attributes: { 'data-element': 'footer-links-grid' }
    },

    // Courses Column
    {
      id: coursesColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Column',
      parentId: linksGridId,
      hierarchyName: getHierarchyName('div'),
      className: linkColumnClass,
      styles: {},
      children: [coursesTitleId, coursesListId],
      text: '',
      attributes: { 'data-element': 'footer-column' }
    },

    // Courses Title
    {
      id: coursesTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Courses Title',
      parentId: coursesColumnId,
      hierarchyName: getHierarchyName('h4'),
      className: columnTitleClass,
      styles: {},
      children: [],
      text: 'Our Courses',
      attributes: { 'data-element': 'column-title' }
    },

    // Courses List
    {
      id: coursesListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'ul',
      name: 'Courses List',
      parentId: coursesColumnId,
      hierarchyName: getHierarchyName('ul'),
      className: linkListClass,
      styles: {},
      children: [webDevLinkId, dataScientLinkId, digitalMarketingLinkId, uiUxLinkId, mobileLinkId, aiMlLinkId, electricianLinkId],
      text: '',
      attributes: { 'data-element': 'link-list' }
    },

    // Computer Operator Link
    {
      id: webDevLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Computer Operator Link Item',
      parentId: coursesListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [webDevLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Computer Operator Link Element
    {
      id: webDevLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Computer Operator Link',
      parentId: webDevLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Computer Operator',
      linkTo: 'computeroperator',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Textile Designing Link
    {
      id: dataScientLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Textile Designing Link Item',
      parentId: coursesListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [dataScientLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Textile Designing Link Element
    {
      id: dataScientLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Textile Designing Link',
      parentId: dataScientLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Textile Designing',
      linkTo: 'textiledesigning',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Digital Marketing Link
    {
      id: digitalMarketingLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Digital Marketing Link Item',
      parentId: coursesListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [digitalMarketingLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Digital Marketing Link Element
    {
      id: digitalMarketingLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Digital Marketing Link',
      parentId: digitalMarketingLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Digital Marketing',
      linkTo: 'digitalmarketing',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Import Export Link
    {
      id: uiUxLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Import Export Link Item',
      parentId: coursesListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [uiUxLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Import Export Link Element
    {
      id: uiUxLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Import Export Link',
      parentId: uiUxLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Import Export Procedure',
      linkTo: 'importexportprocedure',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Dress Making Link
    {
      id: mobileLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Dress Making Link Item',
      parentId: coursesListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [mobileLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Dress Making Link Element
    {
      id: mobileLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Dress Making Link',
      parentId: mobileLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Dress Making',
      linkTo: 'dressmaking',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Beautician Link
    {
      id: aiMlLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Beautician Link Item',
      parentId: coursesListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [aiMlLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Beautician Link Element
    {
      id: aiMlLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Beautician Link',
      parentId: aiMlLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Beautician',
      linkTo: 'beautician',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Electrician Link
    {
      id: electricianLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Electrician Link Item',
      parentId: coursesListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [electricianLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Electrician Link Element
    {
      id: electricianLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Electrician Link',
      parentId: electricianLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Electrician',
      linkTo: 'electrician',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Company Column
    {
      id: companyColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Company Column',
      parentId: linksGridId,
      hierarchyName: getHierarchyName('div'),
      className: linkColumnClass,
      styles: {},
      children: [companyTitleId, companyListId],
      text: '',
      attributes: { 'data-element': 'footer-column' }
    },

    // Company Title
    {
      id: companyTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Company Title',
      parentId: companyColumnId,
      hierarchyName: getHierarchyName('h4'),
      className: columnTitleClass,
      styles: {},
      children: [],
      text: 'Company',
      attributes: { 'data-element': 'column-title' }
    },

    // Company List
    {
      id: companyListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'ul',
      name: 'Company List',
      parentId: companyColumnId,
      hierarchyName: getHierarchyName('ul'),
      className: linkListClass,
      styles: {},
      children: [aboutLinkId, careersLinkId, blogLinkId, pressLinkId, partnersLinkId],
      text: '',
      attributes: { 'data-element': 'link-list' }
    },

    // About Link
    {
      id: aboutLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'About Link Item',
      parentId: companyListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [aboutLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // About Link Element
    {
      id: aboutLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'About Link',
      parentId: aboutLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'About Us',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Careers Link
    {
      id: careersLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Careers Link Item',
      parentId: companyListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [careersLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Careers Link Element
    {
      id: careersLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Careers Link',
      parentId: careersLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Careers',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Blog Link
    {
      id: blogLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Blog Link Item',
      parentId: companyListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [blogLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Blog Link Element
    {
      id: blogLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Blog Link',
      parentId: blogLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Blog',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Press Link
    {
      id: pressLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Press Link Item',
      parentId: companyListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [pressLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Press Link Element
    {
      id: pressLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Press Link',
      parentId: pressLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Press',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Partners Link
    {
      id: partnersLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Partners Link Item',
      parentId: companyListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [partnersLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Partners Link Element
    {
      id: partnersLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Partners Link',
      parentId: partnersLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Partners',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Support Column
    {
      id: supportColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Support Column',
      parentId: linksGridId,
      hierarchyName: getHierarchyName('div'),
      className: linkColumnClass,
      styles: {},
      children: [supportTitleId, supportListId],
      text: '',
      attributes: { 'data-element': 'footer-column' }
    },

    // Support Title
    {
      id: supportTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Support Title',
      parentId: supportColumnId,
      hierarchyName: getHierarchyName('h4'),
      className: columnTitleClass,
      styles: {},
      children: [],
      text: 'Support',
      attributes: { 'data-element': 'column-title' }
    },

    // Support List
    {
      id: supportListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'ul',
      name: 'Support List',
      parentId: supportColumnId,
      hierarchyName: getHierarchyName('ul'),
      className: linkListClass,
      styles: {},
      children: [helpCenterLinkId, contactLinkId, communityLinkId, faqLinkId, statusLinkId],
      text: '',
      attributes: { 'data-element': 'link-list' }
    },

    // Help Center Link
    {
      id: helpCenterLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Help Center Link Item',
      parentId: supportListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [helpCenterLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Help Center Link Element
    {
      id: helpCenterLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Help Center Link',
      parentId: helpCenterLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Help Center',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Contact Link
    {
      id: contactLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Contact Link Item',
      parentId: supportListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [contactLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Contact Link Element
    {
      id: contactLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Contact Link',
      parentId: contactLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Contact Us',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Community Link
    {
      id: communityLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Community Link Item',
      parentId: supportListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [communityLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Community Link Element
    {
      id: communityLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Community Link',
      parentId: communityLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Community',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // FAQ Link
    {
      id: faqLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'FAQ Link Item',
      parentId: supportListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [faqLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // FAQ Link Element
    {
      id: faqLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'FAQ Link',
      parentId: faqLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'FAQ',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Status Link
    {
      id: statusLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Status Link Item',
      parentId: supportListId,
      hierarchyName: getHierarchyName('li'),
      className: linkItemClass,
      styles: {},
      children: [statusLinkId + 100],
      text: '',
      attributes: { 'data-element': 'link-item' }
    },

    // Status Link Element
    {
      id: statusLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Status Link',
      parentId: statusLinkId,
      hierarchyName: getHierarchyName('a'),
      className: linkClass,
      styles: {},
      children: [],
      text: 'Status',
      attributes: { 
        'href': '#',
        'data-element': 'footer-link'
      }
    },

    // Newsletter Section
    {
      id: newsletterSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Newsletter Section',
      parentId: topSectionId,
      hierarchyName: getHierarchyName('div'),
      className: newsletterSectionClass,
      styles: {},
      children: [newsletterTitleId, newsletterDescId, newsletterFormId],
      text: '',
      attributes: { 'data-element': 'newsletter-section' }
    },

    // Newsletter Title
    {
      id: newsletterTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Newsletter Title',
      parentId: newsletterSectionId,
      hierarchyName: getHierarchyName('h4'),
      className: newsletterTitleClass,
      styles: {},
      children: [],
      text: 'Stay Updated',
      attributes: { 'data-element': 'newsletter-title' }
    },

    // Newsletter Description
    {
      id: newsletterDescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Newsletter Description',
      parentId: newsletterSectionId,
      hierarchyName: getHierarchyName('p'),
      className: newsletterDescClass,
      styles: {},
      children: [],
      text: 'Get the latest courses and learning tips delivered to your inbox.',
      attributes: { 'data-element': 'newsletter-desc' }
    },

    // Newsletter Form
    {
      id: newsletterFormId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Newsletter Form',
      parentId: newsletterSectionId,
      hierarchyName: getHierarchyName('form'),
      className: newsletterFormClass,
      styles: {},
      children: [newsletterInputId, newsletterButtonId],
      text: '',
      attributes: { 'data-element': 'newsletter-form' }
    },

    // Newsletter Input
    {
      id: newsletterInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Newsletter Input',
      parentId: newsletterFormId,
      hierarchyName: getHierarchyName('input'),
      className: newsletterInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'email',
        'placeholder': 'Enter your email',
        'required': true,
        'data-element': 'newsletter-input'
      }
    },

    // Newsletter Button
    {
      id: newsletterButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Newsletter Button',
      parentId: newsletterFormId,
      hierarchyName: getHierarchyName('button'),
      className: newsletterButtonClass,
      styles: {},
      children: [],
      text: 'Subscribe',
      attributes: { 
        'type': 'submit',
        'data-element': 'newsletter-button'
      }
    },

    // Bottom Section
    {
      id: bottomSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Bottom Section',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: bottomSectionClass,
      styles: {},
      children: [copyrightId, bottomLinksId],
      text: '',
      attributes: { 'data-element': 'footer-bottom' }
    },

    // Copyright
    {
      id: copyrightId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Copyright',
      parentId: bottomSectionId,
      hierarchyName: getHierarchyName('p'),
      className: copyrightClass,
      styles: {},
      children: [],
      text: '© 2025 SNIT. All rights reserved.',
      attributes: { 'data-element': 'copyright' }
    },

    // Bottom Links
    {
      id: bottomLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'ul',
      name: 'Bottom Links',
      parentId: bottomSectionId,
      hierarchyName: getHierarchyName('ul'),
      className: bottomLinksClass,
      styles: {},
      children: [privacyLinkId, termsLinkId, cookiesLinkId],
      text: '',
      attributes: { 'data-element': 'bottom-links' }
    },

    // Privacy Policy Link
    {
      id: privacyLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Privacy Policy Link',
      parentId: bottomLinksId,
      hierarchyName: getHierarchyName('li'),
      styles: {},
      children: [privacyLinkId + 100],
      text: '',
      attributes: { 'data-element': 'bottom-link' }
    },

    // Privacy Policy Link Element
    {
      id: privacyLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Privacy Policy Link Element',
      parentId: privacyLinkId,
      hierarchyName: getHierarchyName('a'),
      className: bottomLinkClass,
      styles: {},
      children: [],
      text: 'Privacy Policy',
      attributes: { 
        'href': '#',
        'data-element': 'footer-bottom-link'
      }
    },

    // Terms of Service Link
    {
      id: termsLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Terms of Service Link',
      parentId: bottomLinksId,
      hierarchyName: getHierarchyName('li'),
      styles: {},
      children: [termsLinkId + 100],
      text: '',
      attributes: { 'data-element': 'bottom-link' }
    },

    // Terms of Service Link Element
    {
      id: termsLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Terms of Service Link Element',
      parentId: termsLinkId,
      hierarchyName: getHierarchyName('a'),
      className: bottomLinkClass,
      styles: {},
      children: [],
      text: 'Terms of Service',
      attributes: { 
        'href': '#',
        'data-element': 'footer-bottom-link'
      }
    },

    // Cookie Policy Link
    {
      id: cookiesLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: 'Cookie Policy Link',
      parentId: bottomLinksId,
      hierarchyName: getHierarchyName('li'),
      styles: {},
      children: [cookiesLinkId + 100],
      text: '',
      attributes: { 'data-element': 'bottom-link' }
    },

    // Cookie Policy Link Element
    {
      id: cookiesLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Cookie Policy Link Element',
      parentId: cookiesLinkId,
      hierarchyName: getHierarchyName('a'),
      className: bottomLinkClass,
      styles: {},
      children: [],
      text: 'Cookie Policy',
      attributes: { 
        'href': '#',
        'data-element': 'footer-bottom-link'
      }
    }
  ];

  return {
    footerSection: elements[0],
    elements: elements
  };
}; 